// Budgets: spending plans per account, broken into dated periods. The list
// shows each plan; the detail compares planned vs actual spend period by
// period (actuals come from posted journal lines).

import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { StatusDot } from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { type Budget, createBudget, listBudgets } from "@/lib/api";
import { humanizeLabel } from "@/lib/labels";

const PERIOD_TYPES = [
  "monthly",
  "quarterly",
  "yearly",
  "weekly",
  "daily",
  "custom",
] as const;

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

export function BudgetsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const [budgets, setBudgets] = useState<Budget[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(() => {
    listBudgets()
      .then(setBudgets)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  }, []);

  useEffect(reload, [reload]);

  const actions = (
    <Button type="button" size="sm" onClick={() => setCreating(true)}>
      New budget
    </Button>
  );

  return (
    <div className="grid gap-6">
      {embedded ? (
        <div className="actions-row">{actions}</div>
      ) : (
        <PageHeader
          title="Budgets"
          description="Spending plans per account — compare what's planned against what's actually spent."
          actions={actions}
        />
      )}

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
        </div>
      ) : null}

      {budgets === null ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : budgets.length === 0 ? (
        <EmptyState
          title="No budgets yet"
          reason="Set a spending plan per account and track actual spend against it, period by period."
        />
      ) : (
        <div className="surface overflow-hidden rounded-2xl">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Range</TableHead>
                <TableHead>Rhythm</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {budgets.map((budget) => (
                <TableRow key={budget.id}>
                  <TableCell>
                    <Link
                      to="/settings/budgets/$budgetId"
                      params={{ budgetId: budget.id }}
                      className="font-medium hover:underline"
                    >
                      {budget.name}
                    </Link>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs">
                    {new Date(budget.start_date).toLocaleDateString()} →{" "}
                    {new Date(budget.end_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {humanizeLabel(budget.period_type)}
                  </TableCell>
                  <TableCell>
                    <StatusDot
                      tone={budget.is_active ? "info" : "idle"}
                      label={budget.is_active ? "Active" : "Inactive"}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {creating ? (
        <CreateBudgetDrawer
          onClose={() => setCreating(false)}
          onCreated={(budgetId) => {
            reload();
            void navigate({
              to: "/settings/budgets/$budgetId",
              params: { budgetId },
            });
          }}
        />
      ) : null}
    </div>
  );
}

function CreateBudgetDrawer({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (budgetId: string) => void;
}) {
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState(todayInput);
  const [endDate, setEndDate] = useState(todayInput);
  const [periodType, setPeriodType] = useState<(typeof PERIOD_TYPES)[number]>(
    "monthly",
  );
  const [threshold, setThreshold] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (!name.trim()) throw new Error("Name is required.");
      if (!startDate || !endDate)
        throw new Error("Start and end dates are required.");
      if (endDate < startDate)
        throw new Error("The end date must be on or after the start date.");
      let variance_threshold_pct: number | undefined;
      if (threshold.trim()) {
        const value = Number(threshold);
        if (!Number.isInteger(value) || value < 0)
          throw new Error("Variance threshold must be a whole percent.");
        variance_threshold_pct = value;
      }
      const created = await createBudget({
        name: name.trim(),
        start_date: startDate,
        end_date: endDate,
        period_type: periodType,
        variance_threshold_pct,
        is_active: active,
      });
      onCreated(created.id);
      onClose();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title="New budget"
      description="Creates the plan. Account lines and periods are added on the budget."
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <form onSubmit={submit} className="grid gap-3">
        {error ? (
          <p role="alert" className="text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
        <label htmlFor="budget-name" className="grid gap-1 text-xs font-medium">
          Name
          <Input
            id="budget-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            disabled={saving}
          />
        </label>
        <label htmlFor="budget-start" className="grid gap-1 text-xs font-medium">
          Starts
          <Input
            id="budget-start"
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            required
            disabled={saving}
          />
        </label>
        <label htmlFor="budget-end" className="grid gap-1 text-xs font-medium">
          Ends
          <Input
            id="budget-end"
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            required
            disabled={saving}
          />
        </label>
        <label
          htmlFor="budget-period-type"
          className="grid gap-1 text-xs font-medium"
        >
          Rhythm
          <select
            id="budget-period-type"
            className="select"
            value={periodType}
            onChange={(event) =>
              setPeriodType(event.target.value as (typeof PERIOD_TYPES)[number])
            }
            disabled={saving}
          >
            {PERIOD_TYPES.map((type) => (
              <option key={type} value={type}>
                {humanizeLabel(type)}
              </option>
            ))}
          </select>
        </label>
        <label
          htmlFor="budget-threshold"
          className="grid gap-1 text-xs font-medium"
        >
          Variance threshold (%)
          <Input
            id="budget-threshold"
            type="number"
            min="0"
            step="1"
            value={threshold}
            onChange={(event) => setThreshold(event.target.value)}
            placeholder="Optional"
            disabled={saving}
          />
        </label>
        <label className="flex items-center gap-2 text-sm" htmlFor="budget-active">
          <input
            id="budget-active"
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
            disabled={saving}
          />
          Active
        </label>
        <Button type="submit" disabled={saving}>
          {saving ? "Creating…" : "Create budget"}
        </Button>
      </form>
    </Drawer>
  );
}
